import React from 'react'
import YnsIcon from '~/components/icons/YnsIcon'
import { useTranslation } from '~/hooks/useTranslation'

/**
 * AttachmentDisplay component - displays file attachment information
 * Shows file name, size, and type with appropriate icon
 *
 * @param {Object} props
 * @param {Object} props.attachment - Attachment object with name, size, type, url, id
 * @param {boolean} props.isOwnMessage - Whether this is the user's own message
 * @param {Function} props.getDarkModeClasses - Function to get dark mode classes
 */
const AttachmentDisplay = ({
  attachment,
  isOwnMessage,
  getDarkModeClasses,
}) => {
  const { t } = useTranslation('coach_chat')

  if (!attachment) {
    return null
  }

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  // Helper function to get file type icon
  const getFileIcon = (contentType) => {
    if (!contentType) return 'attachment'

    if (contentType.includes('pdf')) return 'document'
    if (contentType.includes('word') || contentType.includes('document'))
      return 'document'
    if (contentType.includes('image')) return 'image'
    if (contentType.includes('text')) return 'document'

    return 'attachment'
  }

  // Helper function to get file type display name
  const getFileTypeName = (contentType) => {
    if (!contentType) return t('attachment.file')

    if (contentType.includes('pdf')) return 'PDF'
    if (contentType.includes('word') || contentType.includes('document'))
      return 'Word Document'
    if (contentType.includes('image')) return 'Image'
    if (contentType.includes('text')) return 'Text File'

    return t('attachment.file')
  }

  // Helper function to force download of file
  const handleDownload = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!attachment.url) return

    try {
      // Fetch the file
      const response = await fetch(attachment.url)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      // Get the blob
      const blob = await response.blob()

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary anchor element and trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = attachment.name || 'download'
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
      // Fallback to opening in new tab if download fails
      window.open(attachment.url, '_blank')
    }
  }

  const fileSize = formatFileSize(attachment.size)
  const fileIcon = getFileIcon(attachment.type)
  const fileTypeName = getFileTypeName(attachment.type)

  return (
    <div className="attachment-display flex items-start gap-3 rounded-lg border border-white border-opacity-20 bg-white bg-opacity-10 p-3">
      {/* File icon */}
      <div className="flex-shrink-0">
        <YnsIcon
          name={fileIcon}
          className={`h-6 w-6 ${
            isOwnMessage
              ? getDarkModeClasses('text-gray-600', 'text-gray-300')
              : 'text-white'
          }`}
        />
      </div>

      {/* File information */}
      <div className="min-w-0 flex-1 overflow-hidden">
        {/* File name */}
        {attachment.url ? (
          <button
            onClick={handleDownload}
            className={`block w-full cursor-pointer truncate text-sm font-medium underline transition-opacity hover:opacity-80 ${
              isOwnMessage
                ? getDarkModeClasses('text-gray-900', 'text-white')
                : 'text-white'
            }`}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
            title={attachment.name}
          >
            {attachment.name}
          </button>
        ) : (
          <div
            className={`block w-full truncate text-sm font-medium ${
              isOwnMessage
                ? getDarkModeClasses('text-gray-900', 'text-white')
                : 'text-white'
            }`}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {attachment.name}
          </div>
        )}

        {/* File type and size */}
        <div
          className={`mt-1 text-xs ${
            isOwnMessage
              ? getDarkModeClasses('text-gray-500', 'text-gray-300')
              : 'text-green-100'
          }`}
        >
          {fileTypeName}
          {fileSize && ` • ${fileSize}`}
        </div>

        {/* Attachment label */}
        <div
          className={`mt-1 text-xs font-medium ${
            isOwnMessage
              ? getDarkModeClasses('text-gray-600', 'text-gray-400')
              : 'text-green-200'
          }`}
        >
          {isOwnMessage
            ? t('attachment.attachment_sent')
            : t('attachment.attachment_received')}
        </div>
      </div>
    </div>
  )
}

export default AttachmentDisplay
